import React from 'react';
import { Flex, Typography, Popover, Tag, Tooltip, Divider, Avatar } from 'antd';
import { Icon } from '@mdi/react';
import dayjs from 'dayjs';
import Ninja from '@ninja';
import JourneyUrlFormatter from './JourneyUrlFormatter';
import JourneyChannelsFormater from './JourneyChannelsFormater';
import CountIcons from './CountIcons';
import ProductImage from './ProductImage';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import {
  AdFormatter,
  UrlFormatter,
} from 'components/tables/NinjaTable/formatters/sourceFormatters';
import getFormattedValue from 'store/redux/utils/getFormattedValue';

import {
  mdiLink,
  mdiBullhornOutline,
  mdiShareVariant,
  mdiAccountDetailsOutline,
  mdiAnchor,
  mdiCalendar,
  mdiAccount,
  mdiOpenInNew,
  mdiIp,
  mdiWeb,
  mdiFinance,
  mdiCash,
} from '@mdi/js';

const TableIcons = ({ icons }) => (
  <div className='flex items-center gap-1'>
    {icons.map((icon, i) => (
      <Tooltip key={i} title={icon.tooltip}>
        <Icon
          path={icon.icon}
          size={0.7}
          color={Ninja.colors.blue}
          onClick={icon.onClick}
          className='cursor-pointer hover:opacity-80'
        />
      </Tooltip>
    ))}
  </div>
);

const CopyableText = ({ value }) => (
  <Tooltip title='Click to copy'>
    <Text
      className='cursor-pointer text-xs text-info hover:underline'
      onClick={() => navigator.clipboard.writeText(value)}
    >
      {value}
    </Text>
  </Tooltip>
);

const { Text } = Typography;

const RowInfo = ({ title, value, icon, children }) => {
  return (
    <div className='group items-start gap-1  -mx-2 px-2 rounded-lg transition-colors mb-2'>
      <div className='grid  grid-cols-4'>
        <Flex align='center' gap='small' className='w-24 sm:col-span-1 col-span-2'>
          <Icon size={0.6} path={icon} color={Ninja.colors.gray} />
          <div className='text-xs font-normal text-dark'>{title}:</div>
        </Flex>
        <span className='sm:col-span-3 col-span-2'>
          {value ? (
            <div className='text-sm font-medium text-color-dark-text break-words'>{value}</div>
          ) : (
            children
          )}
        </span>
      </div>
    </div>
  );
};

const TimelineItem = ({
  title,
  url,
  data,
  trigger = 'hover',
  onClick,
  active,
  type,
  children,
  deviceTagsList,
}) => {
  const user = data?.user || {};
  const ip = data?.ip;
  const website = data?.website || {};
  const deviceTags = deviceTagsList || [];

  const popoverContent = (
    <div className='w-full max-w-[90vw] sm:w-[450px] origin-top-left '>
      <Flex wrap className='w-full' align='center' justify='space-between'>
        <Text className='text-lg whitespace-nowrap font-semibold'>{title}</Text>
        <Flex align='center' gap='small'>
          <Icon path={mdiCalendar} color={Ninja.colors.info} size={0.7} />{' '}
          <Text className='text-sm font-normal text-info'>
            {dayjs(data?.timestamp).format('MMM D, YYYY h:mm A')}
          </Text>
        </Flex>
      </Flex>
      <Divider className='mb-4' />
      {data?.page_url && (
        <RowInfo icon={mdiLink} title='Page URL'>
          <JourneyUrlFormatter url={data?.page_url} />
        </RowInfo>
      )}
      {type == 'event' && (
        <RowInfo icon={mdiCalendar} title='Event ID'>
          <Text copyable className='text-sm font-medium text-center'>
            {data.id}
          </Text>
        </RowInfo>
      )}
      {data?.referrer_url && (
        <RowInfo icon={mdiShareVariant} title='Referrer URL'>
          <JourneyUrlFormatter url={data?.referrer_url} />
        </RowInfo>
      )}
      {user?.id && (
        <RowInfo title='User ID' icon={mdiAccount}>
          <Flex gap='small' align='center' wrap>
            <TableIcons
              icons={[
                {
                  icon: mdiOpenInNew,
                  tooltip: 'See user actions: ads viewed, pages clicked',
                  onClick: () => {
                    window.open(`/user-journey?user_id=${user.id}`, '_blank');
                  },
                },
              ]}
            />
            <Text className='text-sm font-medium text-center'>{user.id || '-'}</Text>
            {user?.identifier?.email && (
              <Text className='text-sm font-medium text-center'>| {user.identifier.email}</Text>
            )}
          </Flex>
        </RowInfo>
      )}
      {ip && <RowInfo title='Address' icon={mdiIp} value={ip} />}
      {website?.host && (
        <RowInfo title='Website' icon={mdiWeb}>
          <Flex align='center' gap='small'>
            <JourneyUrlFormatter
              url={website.host}
              disableCopy
              className='overflow-hidden max-w-32'
            />
            {website?.hash && <CopyableText value={website.hash} />}
          </Flex>
        </RowInfo>
      )}
      {deviceTags.length > 0 && <Divider className='my-2' />}
      {deviceTags.length > 0 && (
        <div className='flex flex-wrap items-center my-2 gap-1'>
          {deviceTagsList.map((tag, i) => (
            <Tooltip key={i} tooltip={tag.tooltip}>
              <Tag
                bordered={false}
                className='flex items-center gap-2 px-2 py-1 text-xs font-medium rounded-md cursor-pointer'
              >
                {tag.icon}
                {tag.value}
              </Tag>
            </Tooltip>
          ))}
        </div>
      )}
      {data?.shopify_event?.product?.id && (
        <RowInfo title='Product' icon={mdiIp}>
          <div className='flex gap-2 items-center'>
            <Tooltip
              title={
                <div className='w-32 grid gap-2 items-center justify-center'>
                  <Avatar
                    src={data?.shopify_event?.product?.media_file?.url}
                    alt='Product'
                    className='object-cover rounded-md !z-50 m-auto h-full w-20'
                  />
                </div>
              }
            >
              <Avatar
                src={data?.shopify_event?.product?.media_file?.url}
                alt='Product'
                className='object-cover rounded-sm !z-50 h-full'
              />
            </Tooltip>
            <h5 className='text-xs'>{data?.shopify_event?.product?.name}</h5>
          </div>
        </RowInfo>
      )}

      {url?.id && <Divider className='my-2' />}
      <div className='gap-2 grid'>
        {url?.id && (
          <div className='flex gap-2 px-2 '>
            <div className='flex items-center gap-1 w-12'>
              <Icon size={0.6} path={mdiLink} color={Ninja.colors.dark} />
              <div className='text-xs font-normal text-dark'>URL :</div>
            </div>
            <div className='max-w-[65%]'>
              <UrlFormatter row={{ url }} />
            </div>
          </div>
        )}
        {url?.ad?.id && (
          <div className='flex gap-2 px-2 '>
            <div className='flex items-center gap-1 w-12'>
              <Icon size={0.6} path={mdiBullhornOutline} color={Ninja.colors.gray} />
              <div className='text-xs font-normal text-dark'>AD:</div>
            </div>
            <div className='max-w-[65%]'>
              <AdFormatter row={url} disablePreview disableJourneyFilter={false} />
            </div>
          </div>
        )}
      </div>

      <Divider className='my-2' />
      <div className='grid sm:grid-cols-3 grid-cols-1 gap-2'>
        <Flex
          className='bg-success-background rounded-md p-1 px-2 border border-solid border-success/30'
          align='center'
          justify='space-between'
        >
          <div>
            <Typography className='text-xs text-color-dark-text font-medium'>Revenue</Typography>
            <Typography className='text-sm text-color-dark-text font-medium'>
              {getFormattedNumber(data?.revenue)}
            </Typography>
          </div>
          <Icon
            path={mdiCash}
            color={Ninja.colors.white}
            size={1.2}
            className='bg-success rounded-md p-1 shadow-md'
          />
        </Flex>
        <Flex
          className='bg-warning-background rounded-md p-1 px-2 border border-solid border-warning/30'
          align='center'
          justify='space-between'
        >
          <div>
            <Typography className='text-xs text-color-dark-text font-medium'>Leads</Typography>
            <Typography className='text-sm text-color-dark-text font-medium'>
              {getFormattedNumber(data?.leads_count)}
            </Typography>
          </div>
          <Icon
            path={mdiAccountDetailsOutline}
            color={Ninja.colors.white}
            size={1.2}
            className='bg-warning rounded-md p-1 shadow-md'
          />
        </Flex>

        <Flex
          className='bg-info-background rounded-md p-1 px-2 border border-solid border-info/30'
          align='center'
          justify='space-between'
        >
          <div>
            <Typography className='text-xs text-color-dark-text font-medium'>Postbacks</Typography>
            <Typography className='text-sm text-color-dark-text font-medium'>
              {getFormattedNumber(data?.postbacks_count)}
            </Typography>
          </div>
          <Icon
            path={mdiAnchor}
            color={Ninja.colors.white}
            size={1.2}
            className='bg-info rounded-md p-1 shadow-md'
          />
        </Flex>
        {data?.payload && (
          <Flex
            className='bg-info-background rounded-md p-1 px-2 border border-solid border-info/30'
            align='center'
            justify='space-between'
          >
            <div>
              <Typography className='text-xs text-color-dark-text font-medium'>Value</Typography>
              <Typography className='text-sm text-color-dark-text font-medium'>
                {getFormattedValue(data?.payload?.value, data?.payload?.currency)}
              </Typography>
            </div>
            <Icon
              path={mdiFinance}
              color={Ninja.colors.white}
              size={1.2}
              className='bg-info rounded-md p-1 shadow-md'
            />
          </Flex>
        )}
      </div>

      {children}
    </div>
  );

  return (
    <Flex
      gap='small'
      align='center'
      className={`w-full transition-all duration-200 transform cursor-pointer items-center hover:scale-[1.01] rounded-md group ${
        active ? 'bg-[#f0f6ff] p-1 px-2' : ''
      }`}
    >
      <Popover
        content={trigger === 'hover' && popoverContent}
        trigger={trigger}
        placement='bottom'
        popupStyle={{ maxWidth: '90vw' }}
      >
        <div
          className='flex flex-col w-full transition-transform duration-200 transform cursor-pointer sm:flex-nowrap hover:scale-[1.01] rounded-md group'
          onClick={trigger !== 'hover' && onClick}
        >
          <Flex gap={4} align='center'>
            <Text
              className={`text-sm whitespace-nowrap text-color-dark-text ${
                active ? 'font-semibold' : 'font-medium group-hover:font-semibold'
              }`}
            >
              {title}
            </Text>
            {((type === 'session' && data?.page_url) ||
              (type === 'event' && data?.type === 'pageview' && data?.page_url)) && (
              <>
                <span className='hidden text-info sm:contents'>|</span>
                <JourneyUrlFormatter
                  url={data.page_url}
                  className='!font-xs !font-medium sm:contents hidden'
                />
              </>
            )}
          </Flex>

          <div className='sm:flex items-center gap-2 grid flex-wrap'>
            <Flex align='center' gap='small'>
              {data?.shopify_event?.product?.id && (
                <ProductImage product={data?.shopify_event?.product} />
              )}
              {data?.channel && (
                <Tag className='rounded-md h-6 flex items-center justify-center m-0 border border-solid border-info-background'>
                  <JourneyChannelsFormater
                    channel={data?.channel}
                    iconProps={{ className: 'text-info hover:text-blue' }}
                  />
                </Tag>
              )}
              {url?.ad?.id && (
                <Tooltip
                  destroyOnHidden
                  className='cursor-pointer'
                  title={
                    <AdFormatter
                      row={url}
                      disablePreview
                      enableReportsFilter={false}
                      disableJourneyFilter={false}
                    />
                  }
                >
                  <Tag className='rounded-md h-6 flex items-center justify-center m-0 border border-solid border-info-background gap-1'>
                    <img src={`/icons/networks/${url?.ad?.network}.svg`} className='w-4 h-4' />
                    <Icon
                      className='text-info hover:text-blue'
                      path={mdiBullhornOutline}
                      size={0.8}
                    />
                  </Tag>
                </Tooltip>
              )}
            </Flex>
            <CountIcons data={data} />
          </div>
        </div>
      </Popover>
    </Flex>
  );
};

export default TimelineItem;
